import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Table, Button, Form, Card, Tab, Tabs, Badge, Alert, Modal } from 'react-bootstrap';
import { FaHammer, FaFire, FaWater, FaCogs, FaPlay, FaRegFileCode, FaSearch, FaMagic, FaCheck, FaTimes, FaCompressAlt, FaExpandAlt, FaSave, FaFolderOpen, FaTrashAlt, FaDownload } from 'react-icons/fa';
import { motion } from 'framer-motion';
import useAuthStore from '../stores/authStore';
import axiosInstance from '../api/axiosInstance';

// --- 전역 스타일 ---
const FORGE_FONTS = "'Pretendard', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif";

// --- 유틸리티: JSON 수선 및 포맷팅 ---
const repairAndFormatJSON = (jsonStr) => {
    if (!jsonStr.trim()) return '';

    // 1. 기본적인 시도: 바로 파싱
    try {
        const obj = JSON.parse(jsonStr);
        return JSON.stringify(obj, null, 2);
    } catch (e) { }

    let repaired = jsonStr;
    try {
        // 2. 단계별 수선 기술 적용
        // - 따옴표 없는 키에 따옴표 추가
        repaired = repaired.replace(/([{,]\s*)([a-zA-Z0-9_$]+)(\s*:)/g, '$1"$2"$3');

        // - 홑따옴표를 쌍따옴표로 변경 (실제 데이터 내의 홑따옴표와 충돌 가능성 무시하고 시도)
        repaired = repaired.replace(/'/g, '"');

        // - 트레일링 콤마 제거 (쉼표로 끝나는 배열/객체)
        repaired = repaired.replace(/,\s*([}\]])/g, '$1');

        // - 줄 단위 수선: 닫히지 않은 문자열 끝내기
        repaired = repaired.split('\n').map(line => {
            const trimmed = line.trim();
            if (trimmed.includes(':')) {
                const parts = line.split(':');
                const lastPart = parts[parts.length - 1].trim();
                // 따옴표 개수가 홀수면 닫히지 않은 것으로 간주하고 닫아줌
                const cleanLast = lastPart.replace(/\\"/g, 'XX'); // 이스케이프된 따옴표 무시
                const quoteCount = (cleanLast.match(/"/g) || []).length;
                if (quoteCount % 2 !== 0 && !lastPart.endsWith('"')) {
                    return line + '"';
                }
            }
            return line;
        }).join('\n');

        // - 중괄호/대괄호 짝 맞추기
        const openBraces = (repaired.match(/{/g) || []).length;
        const closeBraces = (repaired.match(/}/g) || []).length;
        if (openBraces > closeBraces) repaired += '}'.repeat(openBraces - closeBraces);

        const openBracks = (repaired.match(/\[/g) || []).length;
        const closeBracks = (repaired.match(/\]/g) || []).length;
        if (openBracks > closeBracks) repaired += ']'.repeat(openBracks - closeBracks);

        // 수선된 결과 시도
        try {
            const obj = JSON.parse(repaired);
            return JSON.stringify(obj, null, 2);
        } catch (e3) {
            // 3. 최후의 수단: JS 객체 모델로 평가 (Function 생성자)
            // eslint-disable-next-line no-new-func
            const repairedEval = new Function('return (' + repaired + ')')();
            return JSON.stringify(repairedEval, null, 2);
        }
    } catch (err) {
        // 원본으로라도 마지막 시도
        try {
            // eslint-disable-next-line no-new-func
            const originalEval = new Function('return (' + jsonStr + ')')();
            return JSON.stringify(originalEval, null, 2);
        } catch (err2) {
            return null;
        }
    }
};

// --- 공통 컴포넌트: 라인 번호가 있는 코드 에디터 ---
const CodeEditor = ({ value, onChange, placeholder, rows = 15, readOnly = false }) => {
    const lineNumbers = value.split('\n').length;
    const textareaRef = useRef(null);
    const lineNumbersRef = useRef(null);

    const handleScroll = (e) => {
        if (lineNumbersRef.current) {
            lineNumbersRef.current.scrollTop = e.target.scrollTop;
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            const start = e.target.selectionStart;
            const end = e.target.selectionEnd;
            const target = e.target;
            const val = target.value;

            // 탭 대신 공백 2개 삽입
            target.value = val.substring(0, start) + '  ' + val.substring(end);
            target.selectionStart = target.selectionEnd = start + 2;

            // 상태 업데이트 트리거
            onChange({ target: { value: target.value } });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const start = e.target.selectionStart;
            const end = e.target.selectionEnd;
            const target = e.target;
            const val = target.value;

            // 현재 커서가 위치한 줄의 텍스트와 인덴트 추출
            const linesBefore = val.substring(0, start).split('\n');
            const currentLine = linesBefore[linesBefore.length - 1];
            const indentMatch = currentLine.match(/^\s*/);
            const indent = indentMatch ? indentMatch[0] : '';

            const charBefore = val[start - 1];
            const charAfter = val[start];

            // 1. 커서가 {} 나 [] 사이에 있는 경우 (스마트 인덴트)
            if (start === end && ((charBefore === '{' && charAfter === '}') || (charBefore === '[' && charAfter === ']'))) {
                const newValue = `${val.substring(0, start)}\n${indent}  \n${indent}${val.substring(end)}`;
                target.value = newValue;
                target.selectionStart = target.selectionEnd = start + 1 + indent.length + 2;
            }
            // 2. 일반적인 인덴트 처리
            else {
                let nextIndent = indent;
                const trimmedLine = currentLine.trim();
                if (trimmedLine.endsWith('{') || trimmedLine.endsWith('[')) {
                    nextIndent += '  ';
                }
                const newValue = val.substring(0, start) + '\n' + nextIndent + val.substring(end);
                target.value = newValue;
                target.selectionStart = target.selectionEnd = start + 1 + nextIndent.length;
            }

            onChange({ target: { value: target.value } });
        } else if (['{', '[', '(', '"', "'"].includes(e.key)) {
            e.preventDefault();
            const start = e.target.selectionStart;
            const end = e.target.selectionEnd;
            const target = e.target;
            const val = target.value;

            const pairs = { '{': '}', '[': ']', '(': ')', '"': '"', "'": "'" };
            const closeChar = pairs[e.key];

            // 텍스트 선택 중이면 선택 영역을 감쌈
            const selectedText = val.substring(start, end);
            const newValue = val.substring(0, start) + e.key + selectedText + closeChar + val.substring(end);

            target.value = newValue;
            // 커서를 시작 기호 바로 뒤로 이동 (선택 텍스트가 있었다면 유지하되 앞뒤로 기호가 붙음)
            if (selectedText) {
                target.selectionStart = start + 1;
                target.selectionEnd = end + 1;
            } else {
                target.selectionStart = target.selectionEnd = start + 1;
            }

            onChange({ target: { value: target.value } });
        } else if (['}', ']', ')', '"', "'"].includes(e.key)) {
            const start = e.target.selectionStart;
            const target = e.target;
            const val = target.value;

            // 만약 현재 커서 위치 뒤에 같은 기호가 있다면 입력하는 대신 커서만 이동 (Skip)
            if (val[start] === e.key && start === e.target.selectionEnd) {
                e.preventDefault();
                target.selectionStart = target.selectionEnd = start + 1;
            }
        } else if (e.key === 'Backspace') {
            const start = e.target.selectionStart;
            const end = e.target.selectionEnd;
            const target = e.target;
            const val = target.value;

            // 커서가 괄호 쌍 사이에 있을 때 (예: {} ) 백스페이스 누르면 뒤의 괄호도 함께 삭제
            if (start === end && start > 0) {
                const charBefore = val[start - 1];
                const charAfter = val[start];
                const pairs = { '{': '}', '[': ']', '(': ')', '"': '"', "'": "'" };

                if (pairs[charBefore] === charAfter) {
                    e.preventDefault();
                    const newValue = val.substring(0, start - 1) + val.substring(start + 1);
                    target.value = newValue;
                    target.selectionStart = target.selectionEnd = start - 1;
                    onChange({ target: { value: target.value } });
                }
            }
        }
    };

    return (
        <div className="d-flex border rounded-3 overflow-hidden font-monospace bg-white shadow-sm" style={{ position: 'relative', height: '500px' }}>
            <div
                ref={lineNumbersRef}
                className="bg-light text-muted text-end px-2 py-2 border-end select-none scrollbar-hide"
                style={{
                    minWidth: '60px',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    overflow: 'hidden',
                    backgroundColor: '#f8f9fa',
                    height: '100%',
                    whiteSpace: 'nowrap'
                }}
            >
                {Array.from({ length: lineNumbers }).map((_, i) => (
                    <div key={i}>{i + 1}</div>
                ))}
            </div>
            <textarea
                ref={textareaRef}
                value={value}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                onScroll={handleScroll}
                placeholder={placeholder}
                readOnly={readOnly}
                className="form-control border-0 p-2 custom-scrollbar"
                style={{
                    fontSize: '13px',
                    lineHeight: '1.5',
                    resize: 'none',
                    whiteSpace: 'pre',
                    overflowWrap: 'normal',
                    outline: 'none',
                    boxShadow: 'none',
                    height: '100%',
                    overflow: 'auto',
                    borderRadius: '0'
                }}
            />
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #ccc;
                    border-radius: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #bbb;
                }
                .scrollbar-hide::-webkit-scrollbar { display: none; } 
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div>
    );
};

// --- JSON 트리 뷰 컴포넌트 ---
const JsonTreeView = ({ data, label }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const renderLabel = () => label !== undefined ? <span className="json-key">{label}:</span> : null;

    if (data === null || typeof data !== 'object') {
        return (
            <div className="json-row">
                {renderLabel()}
                <span className="json-value">
                    {data === null ? <span className="text-muted">null</span> :
                        typeof data === 'string' ? <span style={{ color: '#0984e3' }}>"{data}"</span> :
                            typeof data === 'number' ? <span style={{ color: '#e17055' }}>{data}</span> :
                                <span style={{ color: '#00b894' }}>{data.toString()}</span>}
                </span>
            </div>
        );
    }

    const isArray = Array.isArray(data);
    const size = isArray ? data.length : Object.keys(data).length;

    return (
        <div className="json-tree-node">
            <div className="json-toggle-row" onClick={() => setIsExpanded(!isExpanded)} style={{ cursor: 'pointer' }}>
                <span className="json-toggle-icon">{isExpanded ? '▼' : '▶'}</span>
                {renderLabel()}
                <span className={`json-meta opacity-75 small ${isArray ? 'text-danger' : 'text-primary'}`}>
                    {isArray ? `[ ${size} ]` : `{ ${size} }`}
                </span>
            </div>
            {isExpanded && (
                <div className="json-children">
                    {Object.entries(data).map(([key, value]) => (
                        <JsonTreeView key={key} data={value} label={key} />
                    ))}
                </div>
            )}
        </div>
    );
};

// --- 라이브러리 프리셋 ---
const LIBRARIES = {
    js: [
        { name: 'None', url: null },
        { name: 'jQuery 3.7.1', url: 'https://code.jquery.com/jquery-3.7.1.min.js' },
        { name: 'jQuery 2.2.4', url: 'https://code.jquery.com/jquery-2.2.4.min.js' },
        { name: 'React 18', url: 'https://unpkg.com/react@18/umd/react.development.js' },
        { name: 'ReactDOM 18', url: 'https://unpkg.com/react-dom@18/umd/react-dom.development.js' },
        { name: 'Vue 3', url: 'https://unpkg.com/vue@3/dist/vue.global.js' },
        { name: 'Axios', url: 'https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js' },
        { name: 'Lodash', url: 'https://cdn.jsdelivr.net/npm/lodash@4.17.21/lodash.min.js' },
        { name: 'GSAP', url: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js' },
    ],
    css: [
        { name: 'None', url: null },
        { name: 'Bootstrap 5.3', url: 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css' },
        { name: 'Tailwind CSS (Play)', url: 'https://cdn.tailwindcss.com' },
        { name: 'Animate.css', url: 'https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css' },
        { name: 'Font Awesome 6', url: 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css' },
    ]
};

// --- 메인 컴포넌트 ---
export const DevToolbox = () => {
    const [activeTab, setActiveTab] = useState('json');

    // JSON Editor State
    const [jsonInput, setJsonInput] = useState('');
    const [jsonError, setJsonError] = useState('');

    // Diff Checker State
    const [diffLeft, setDiffLeft] = useState('{\n  "name": "Forge",\n  "version": "1.0"\n}');
    const [diffRight, setDiffRight] = useState('{\n  "name": "Dev Forge",\n  "version": "1.1",\n  "status": "active"\n}');
    const [diffResult, setDiffResult] = useState({ lines: [], removals: 0, additions: 0 });

    // Playground State
    const [playgroundSettings, setPlaygroundSettings] = useState({
        html: { lang: 'HTML', content: '<h1>Hello Forge!</h1>', libs: [], doctype: 'HTML 5', bodyTag: '' },
        css: { lang: 'CSS', content: 'h1 { color: #d9534f; text-align: center; }', libs: [] },
        js: { lang: 'JavaScript', content: 'console.log("Forge active!");\n// jQuery가 로드되었다면 사용 가능:\n// $("h1").append(" 🌊");', libs: [] }
    });
    const [previewContent, setPreviewContent] = useState('');
    const [showOptionsModal, setShowOptionsModal] = useState(false);
    const [activePanelTab, setActivePanelTab] = useState('html'); // 모달 내부 탭

    // CRUD State
    const { isAuthenticated } = useAuthStore();
    const [showLoadModal, setShowLoadModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [savedItems, setSavedItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [tabStates, setTabStates] = useState({
        json: { id: null, title: '' },
        diff: { id: null, title: '' },
        playground: { id: null, title: '' }
    });
    // 모달에서 사용하는 임시 제목 상태
    const [tempSaveTitle, setTempSaveTitle] = useState('');
    const itemsPerPage = 10;

    // 저장된 목록 가져오기
    const fetchSavedItems = useCallback(async (page = 1) => {
        if (!isAuthenticated) return;
        setIsLoadingList(true);
        try {
            const offset = (page - 1) * itemsPerPage;
            const response = await axiosInstance.get(`posts?board_id=3&OFFSET=${offset}`);
            if (response.data.status === '0000') {
                // n8n에서 데이터가 .json 안에 들어오는 경우에 대응하여 평탄화
                const rawList = response.data.data.postList || [];
                const formattedList = rawList.map(item => item.json || item);
                setSavedItems(formattedList);
                setTotalCount(response.data.data.postCount || 1);
            } else {
                setSavedItems([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Failed to fetch saved items:', error);
        } finally {
            setIsLoadingList(false);
        }
    }, [isAuthenticated]);

    // 저장/수정 실행
    const handleSave = async (e) => {
        if (e) e.preventDefault();

        const currentId = tabStates[activeTab].id;
        // 저장 모달을 통해 제목을 처음 입력받거나, 기존 제목을 사용
        const targetTitle = tempSaveTitle.trim() || tabStates[activeTab].title;

        if (!targetTitle) {
            alert('제목을 입력해주세요.');
            return;
        }

        setIsSaving(true);
        try {
            let currentData = {};
            if (activeTab === 'json') {
                currentData = { tab: 'json', input: jsonInput };
            } else if (activeTab === 'diff') {
                currentData = { tab: 'diff', left: diffLeft, right: diffRight };
            } else if (activeTab === 'playground') {
                currentData = {
                    tab: 'playground',
                    code: {
                        html: playgroundSettings.html.content,
                        css: playgroundSettings.css.content,
                        script: playgroundSettings.js.content
                    },
                    options: {
                        html: { lang: playgroundSettings.html.lang, doctype: playgroundSettings.html.doctype, bodyTag: playgroundSettings.html.bodyTag },
                        css: { lang: playgroundSettings.css.lang, libs: playgroundSettings.css.libs },
                        js: { lang: playgroundSettings.js.lang, libs: playgroundSettings.js.libs }
                    }
                };
            }

            const payload = {
                slug: 'dev-tool-box',
                title: targetTitle,
                extra_data: currentData,
                is_notice: true
            };

            let response;
            if (currentId) {
                // 수정
                response = await axiosInstance.put('posts', { ...payload, id: currentId });
            } else {
                // 신규 등록
                response = await axiosInstance.post('posts', payload);
            }

            if (response.data.status === '0000') {
                alert(response.data.message);
                setShowSaveModal(false);
                setTempSaveTitle('');

                // 해당 탭의 상태 업데이트
                const newId = currentId || response.data.data?.id;
                setTabStates(prev => ({
                    ...prev,
                    [activeTab]: { id: newId, title: targetTitle }
                }));

                fetchSavedItems(currentPage);
            } else {
                alert(response.data.message || '저장에 실패했습니다.');
            }
        } catch (error) {
            console.error('Save error:', error);
            alert('서버 통신 중 오류가 발생했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    // 불러오기 처리
    const handleLoadItem = async (item) => {
        // 상세 정보 조회가 필요한 경우 (목록에 extra_data가 없는 경우)
        if (!item.extra_data) {
            try {
                const response = await axiosInstance.get(`postDetail?post_id=${item.id}`);
                if (response.data.status === '0000') {
                    // 상세 정보 역시 .json 안에 들어올 수 있음
                    const detail = response.data.data.postList[0];
                    applyItemData(detail.json || detail);
                }
            } catch (error) {
                console.error('Load detail error:', error);
                alert('상세 정보를 불러오지 못했습니다.');
            }
        } else {
            applyItemData(item);
        }
    };

    const applyItemData = (item) => {
        const data = item.extra_data;
        if (!data || !data.tab) {
            alert('데이터 형식이 올바르지 않습니다.');
            return;
        }

        // 현재 탭 정보 업데이트
        setTabStates(prev => ({
            ...prev,
            [data.tab]: { id: item.id, title: item.title }
        }));

        setActiveTab(data.tab);

        if (data.tab === 'json') {
            setJsonInput(data.input || '');
        } else if (data.tab === 'diff') {
            setDiffLeft(data.left || '');
            setDiffRight(data.right || '');
        } else if (data.tab === 'playground') {
            setPlaygroundSettings({
                html: {
                    lang: data.options?.html?.lang || 'HTML',
                    content: data.code?.html || '',
                    libs: [],
                    doctype: data.options?.html?.doctype || 'HTML 5',
                    bodyTag: data.options?.html?.bodyTag || ''
                },
                css: {
                    lang: data.options?.css?.lang || 'CSS',
                    content: data.code?.css || '',
                    libs: data.options?.css?.libs || []
                },
                js: {
                    lang: data.options?.js?.lang || 'JavaScript',
                    content: data.code?.script || '',
                    libs: data.options?.js?.libs || []
                }
            });
        }
        setShowLoadModal(false);
    };

    // 삭제 처리
    const handleDelete = async (id) => {
        if (!window.confirm('정말로 삭제하시겠습니까?')) return;
        try {
            const response = await axiosInstance.delete(`posts`, { data: { id } });
            if (response.data.status === '0000') {
                alert('삭제되었습니다.');

                // 탭별 상태 중 삭제된 ID가 있으면 초기화
                setTabStates(prev => {
                    const next = { ...prev };
                    Object.keys(next).forEach(tab => {
                        if (next[tab].id === id) {
                            next[tab] = { id: null, title: '' };
                        }
                    });
                    return next;
                });
                fetchSavedItems(currentPage);
            }
        } catch (error) {
            console.error('Delete error:', error);
        }
    };

    // JSON 다운로드
    const handleJsonDownload = () => {
        try {
            if (!jsonInput.trim()) {
                alert('내보낼 데이터가 없습니다.');
                return;
            }
            JSON.parse(jsonInput);
            const blob = new Blob([jsonInput], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `forge_${new Date().getTime()}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            alert('유효하지 않은 JSON 형식입니다. 먼저 수선하거나 수정해주세요.');
        }
    };

    // Playground 다운로드 (통합 HTML)
    const handlePlaygroundDownload = () => {
        const { html, css, js } = playgroundSettings;
        const libsHtml = [
            ...css.libs.map(url => `<link rel="stylesheet" href="${url}">`),
            ...js.libs.map(url => `<script src="${url}"></script>`)
        ].join('\n    ');

        const fullHtml = `<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Forge Playground Export</title>
    ${libsHtml}
    <style>
${css.content}
    </style>
</head>
<body>
${html.content}
    <script>
${js.content}
    </script>
</body>
</html>`;

        const blob = new Blob([fullHtml], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `forge_playground_${new Date().getTime()}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        if (showLoadModal) fetchSavedItems(currentPage);
    }, [showLoadModal, currentPage, fetchSavedItems]);

    // JSON 정렬
    const handleJsonFormat = () => {
        const formatted = repairAndFormatJSON(jsonInput);
        if (formatted) {
            setJsonInput(formatted);
            setJsonError('');
        } else {
            setJsonError('❌ 이 데이터는 제련이 불가능합니다. (형식 오류)');
        }
    };

    // JSON 자동 수선
    const handleJsonRepair = () => {
        const repaired = repairAndFormatJSON(jsonInput);
        if (repaired) {
            setJsonInput(repaired);
            setJsonError('');
        } else {
            setJsonError('❌ 자동 수선에 실패했습니다. 직접 고철을 골라내야 할 것 같네요.');
        }
    };

    // JSON 압축
    const handleJsonMinify = () => {
        if (!jsonInput.trim()) return;
        try {
            let obj;
            try {
                obj = JSON.parse(jsonInput);
            } catch (e) {
                // eslint-disable-next-line no-new-func
                obj = new Function('return (' + jsonInput + ')')();
            }
            setJsonInput(JSON.stringify(obj));
            setJsonError('');
        } catch (e) {
            setJsonError('❌ 압축에 실패했습니다. 데이터 형식을 확인해 주세요.');
        }
    };

    // Diff 계산
    const handleCheckDiff = useCallback(() => {
        const linesLeft = diffLeft.split('\n');
        const linesRight = diffRight.split('\n');
        const maxLines = Math.max(linesLeft.length, linesRight.length);
        const resultLines = [];
        let rCount = 0;
        let aCount = 0;

        for (let i = 0; i < maxLines; i++) {
            const left = linesLeft[i] === undefined ? null : linesLeft[i];
            const right = linesRight[i] === undefined ? null : linesRight[i];
            const isDiff = left !== right;

            if (isDiff) {
                if (left !== null) rCount++;
                if (right !== null) aCount++;
            }

            resultLines.push({ left, right, isDiff });
        }
        setDiffResult({ lines: resultLines, removals: rCount, additions: aCount });
    }, [diffLeft, diffRight]);

    useEffect(() => {
        if (activeTab === 'diff') handleCheckDiff();
    }, [activeTab, handleCheckDiff]);

    // Playground 가동
    const runPlayground = useCallback(() => {
        const jsLibs = playgroundSettings.js.libs.map(lib => `<script src="${lib}"></script>`).join('\n');
        const cssLibs = playgroundSettings.css.libs.map(lib => `<link rel="stylesheet" href="${lib}">`).join('\n');

        // Tailwind 특수 처리
        const isTailwind = playgroundSettings.css.libs.includes('https://cdn.tailwindcss.com');

        const doctypeMap = {
            'HTML 5': '<!DOCTYPE html>',
            'XHTML 1.0 Strict': '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">',
            'XHTML 1.0 Transitional': '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">',
            'HTML 4.01 Strict': '<!DOCTYPE HTML PUBLIC "-//W3C//DTD HTML 4.01//EN" "http://www.w3.org/TR/html4/strict.dtd">',
            'HTML 4.01 Transitional': '<!DOCTYPE HTML PUBLIC "-//W3C//DTD HTML 4.01 Transitional//EN" "http://www.w3.org/TR/html4/loose.dtd">',
            'HTML 4.01 Frameset': '<!DOCTYPE HTML PUBLIC "-//W3C//DTD HTML 4.01 Frameset//EN" "http://www.w3.org/TR/html4/frameset.dtd">'
        };

        const doctype = doctypeMap[playgroundSettings.html.doctype] || '<!DOCTYPE html>';
        const bodyTagAttr = playgroundSettings.html.bodyTag || '';

        const combined = `
            ${doctype}
            <html ${playgroundSettings.html.doctype?.includes('XHTML') ? 'xmlns="http://www.w3.org/1999/xhtml"' : ''}>
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    ${cssLibs}
                    ${isTailwind ? '<script src="https://cdn.tailwindcss.com"></script>' : ''}
                    <style>${playgroundSettings.css.content}</style>
                </head>
                <body ${bodyTagAttr}>
                    ${playgroundSettings.html.content}
                    ${jsLibs}
                    <script>${playgroundSettings.js.content}</script>
                </body>
            </html>
        `;
        setPreviewContent(combined);
    }, [playgroundSettings]);

    return (
        <Container className="mt-4 mb-5 bg-white min-vh-100 dev-toolbox-container" style={{ fontFamily: FORGE_FONTS }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="mb-0"><FaHammer className="text-secondary" /> 제련도구</h2>
            </div>

            <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4 custom-forge-tabs border-0 justify-content-center"
            >
                {/* 1. JSON 에디터 */}
                <Tab eventKey="json" title={<span><FaFire className="me-1 text-danger" /> 데이터 담금질</span>}>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4">
                        <Row className="g-3">
                            <Col lg={7}>
                                <Card className="border shadow-sm rounded-4 overflow-hidden">
                                    <div className="bg-light p-2 border-bottom d-flex justify-content-between align-items-center px-3 json-editor-header">
                                        <div className="d-flex align-items-center gap-2 json-header-label">
                                            <FaRegFileCode className="text-secondary" />
                                            <span className="fw-bold small">JSON 입력</span>
                                        </div>
                                        <div className="d-flex gap-1 json-header-btns">
                                            {isAuthenticated && (
                                                <div className="d-flex gap-1 me-2 pe-2 border-end border-secondary border-opacity-25 json-crud-btns">
                                                    <Button variant="outline-success" size="sm" className="rounded-pill px-2 py-1" onClick={() => setShowLoadModal(true)}>
                                                        <FaFolderOpen className="me-1" /> 불러오기
                                                    </Button>
                                                    <Button variant="success" size="sm" className="rounded-pill px-2 py-1" onClick={() => {
                                                        if (tabStates.json.id) handleSave();
                                                        else setShowSaveModal(true);
                                                    }}>
                                                        <FaSave className="me-1" /> 저장
                                                    </Button>
                                                    {tabStates.json.id && (
                                                        <Button variant="outline-primary" size="sm" className="rounded-pill px-2 py-1" onClick={() => {
                                                            const oldTitle = tabStates.json.title;
                                                            setTabStates(prev => ({ ...prev, json: { id: null, title: '' } }));
                                                            setTempSaveTitle(oldTitle);
                                                            setShowSaveModal(true);
                                                        }}>
                                                            다른 이름
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                            <div className="d-flex gap-1 json-util-btns">
                                                <Button variant="outline-dark" size="sm" className="rounded-pill px-2 py-1" onClick={handleJsonDownload}>
                                                    <FaDownload className="me-1" /> 추출
                                                </Button>
                                                <Button variant="outline-primary" size="sm" className="rounded-pill px-2 py-1 text-nowrap" onClick={handleJsonRepair}>
                                                    <FaMagic className="me-1" /> 자동 수선
                                                </Button>
                                                <Button variant="outline-secondary" size="sm" className="rounded-pill px-2 py-1" onClick={handleJsonMinify}>
                                                    <FaCompressAlt className="me-1" /> 압축
                                                </Button>
                                                <Button variant="primary" size="sm" className="rounded-pill px-2 py-1" onClick={handleJsonFormat}>
                                                    <FaExpandAlt className="me-1" /> 펼치기
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-0">
                                        <CodeEditor
                                            value={jsonInput}
                                            onChange={(e) => setJsonInput(e.target.value)}
                                            placeholder='{ "고철": "데이터를 넣어주세요" }'
                                            rows={20}
                                        />
                                    </div>
                                </Card>
                                {jsonError && <Alert variant="danger" className="mt-3 p-2 small transition-all">{jsonError}</Alert>}
                            </Col>
                            <Col lg={5}>
                                <Card className="border shadow-sm rounded-4 overflow-hidden h-100">
                                    <div className="bg-light p-2 border-bottom px-3">
                                        <span className="fw-bold small"><FaSearch className="me-1 text-secondary" /> 제련된 뼈대</span>
                                    </div>
                                    <div className="p-3 overflow-auto font-monospace custom-scrollbar" style={{ height: '500px', backgroundColor: '#fafafa' }}>
                                        {jsonInput ? (
                                            <div style={{ display: 'inline-block', minWidth: '100%' }}>
                                                <JsonTreeView data={(() => {
                                                    try { return JSON.parse(jsonInput); } catch (e) {
                                                        try {
                                                            // eslint-disable-next-line no-new-func
                                                            return new Function('return (' + jsonInput + ')')();
                                                        } catch (e2) { return null; }
                                                    }
                                                })()} />
                                            </div>
                                        ) : (
                                            <div className="text-center text-muted py-5 mt-5">
                                                <FaCogs size={30} className="mb-2 opacity-25" />
                                                <p className="small">왼쪽에 데이터를 입력하면<br />트리 구조로 분석해 드립니다.</p>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </motion.div>
                </Tab>

                {/* 2. 코드 비교질 (Diff) */}
                <Tab eventKey="diff" title={<span><FaWater className="me-1 text-primary" /> 코드 비교질</span>}>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="d-flex gap-2">
                                <Badge bg="danger" className="rounded-pill py-2 px-3 fw-normal">
                                    <FaTimes className="me-1" /> {diffResult.removals} removals
                                </Badge>
                                <Badge bg="success" className="rounded-pill py-2 px-3 fw-normal">
                                    <FaCheck className="me-1" /> {diffResult.additions} additions
                                </Badge>
                            </div>
                            <Button variant="primary" size="sm" className="rounded-pill px-4 shadow-sm" onClick={handleCheckDiff}>
                                <FaSearch className="me-2" /> 다시 비교하기
                            </Button>
                            {isAuthenticated && (
                                <div className="d-flex gap-1 border-start ps-3 ms-auto diff-crud-btns">
                                    <Button variant="outline-success" size="sm" className="rounded-pill px-3" onClick={() => setShowLoadModal(true)}>
                                        <FaFolderOpen className="me-1" /> 불러오기
                                    </Button>
                                    <Button variant="success" size="sm" className="rounded-pill px-3" onClick={() => {
                                        if (tabStates.diff.id) handleSave();
                                        else setShowSaveModal(true);
                                    }}>
                                        <FaSave className="me-1" /> 저장
                                    </Button>
                                    {tabStates.diff.id && (
                                        <Button variant="outline-primary" size="sm" className="rounded-pill px-3" onClick={() => {
                                            const oldTitle = tabStates.diff.title;
                                            setTabStates(prev => ({ ...prev, diff: { id: null, title: '' } }));
                                            setTempSaveTitle(oldTitle);
                                            setShowSaveModal(true);
                                        }}>
                                            다른 이름
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>

                        <Row className="g-3">
                            <Col md={6}>
                                <div className="mb-2 ms-2 small fw-bold text-muted">원본 장비 (Original)</div>
                                <CodeEditor
                                    value={diffLeft}
                                    onChange={(e) => setDiffLeft(e.target.value)}
                                    rows={10}
                                />
                            </Col>
                            <Col md={6}>
                                <div className="mb-2 ms-2 small fw-bold text-muted">개조된 장비 (Changed)</div>
                                <CodeEditor
                                    value={diffRight}
                                    onChange={(e) => setDiffRight(e.target.value)}
                                    rows={10}
                                />
                            </Col>
                        </Row>

                        <Card className="mt-4 border-0 shadow-sm rounded-4 overflow-hidden">
                            <div className="bg-light p-2 border-bottom px-3 fw-bold small text-secondary">
                                제련 결과 대비표 (Side-by-Side)
                            </div>
                            <div className="p-0 overflow-auto scrollbar-hide" style={{ maxHeight: '500px' }}>
                                <Table hover className="mb-0 font-monospace" style={{ fontSize: '13px', tableLayout: 'fixed' }}>
                                    <tbody>
                                        {diffResult.lines.map((line, idx) => (
                                            <tr key={idx} style={{ height: '24px' }}>
                                                <td className="text-muted border-end text-end p-0 px-2 line-num-cell" style={{ width: '40px', backgroundColor: '#f8f9fa' }}>{idx + 1}</td>
                                                <td className={`p-1 px-3 w-50 border-end ${line.isDiff && line.left !== null ? 'bg-danger bg-opacity-10' : ''}`} style={{ whiteSpace: 'pre', overflow: 'hidden' }}>
                                                    {line.left}
                                                </td>
                                                <td className={`p-1 px-3 w-50 ${line.isDiff && line.right !== null ? 'bg-success bg-opacity-10' : ''}`} style={{ whiteSpace: 'pre', overflow: 'hidden' }}>
                                                    {line.right}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card>
                    </motion.div>
                </Tab>

                {/* 3. 코드 용광로 (Playground) */}
                <Tab eventKey="playground" title={<span><FaHammer className="me-1 text-secondary" /> 코드 용광로</span>}>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4">
                        <Row className="g-4">
                            <Col lg={6}>
                                <div className="d-flex flex-column gap-3">
                                    {['html', 'css', 'js'].map((panel) => (
                                        <Card key={panel} className="border shadow-sm rounded-4 overflow-hidden">
                                            <div className="bg-light p-2 border-bottom d-flex justify-content-between align-items-center px-3">
                                                <div
                                                    className="d-flex align-items-center gap-2 cursor-pointer"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => { setActivePanelTab(panel); setShowOptionsModal(true); }}
                                                >
                                                    <Badge bg={panel === 'html' ? 'danger' : panel === 'css' ? 'primary' : 'warning'} className="text-uppercase">
                                                        {playgroundSettings[panel].lang}
                                                    </Badge>
                                                    <span className="small text-muted fw-bold">
                                                        {playgroundSettings[panel].libs.length > 0 && `+${playgroundSettings[panel].libs.length} Libs`}
                                                    </span>
                                                </div>
                                                <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => { setActivePanelTab(panel); setShowOptionsModal(true); }}>
                                                    <FaCogs size={14} />
                                                </Button>
                                            </div>
                                            <div className="p-0">
                                                <CodeEditor
                                                    value={playgroundSettings[panel].content}
                                                    onChange={(e) => setPlaygroundSettings({
                                                        ...playgroundSettings,
                                                        [panel]: { ...playgroundSettings[panel], content: e.target.value }
                                                    })}
                                                    rows={6}
                                                />
                                            </div>
                                        </Card>
                                    ))}
                                    <div className="d-flex gap-2">
                                        <Button variant="danger" className="py-3 fw-bold rounded-4 shadow-sm border-0 mt-2 flex-grow-1" onClick={runPlayground}>
                                            <FaPlay className="me-2" /> 연성하기 (Run)
                                        </Button>
                                        {isAuthenticated && (
                                            <div className="d-flex gap-2 flex-grow-1 playground-crud-btns">
                                                <Button variant="outline-success" className="py-3 fw-bold rounded-4 shadow-sm mt-2 flex-grow-1" style={{ minWidth: '100px' }} onClick={() => setShowLoadModal(true)}>
                                                    <FaFolderOpen className="me-2" /> 불러오기
                                                </Button>
                                                <Button variant="success" className="py-3 fw-bold rounded-4 shadow-sm mt-2 flex-grow-1" style={{ minWidth: '100px' }} onClick={() => {
                                                    if (tabStates.playground.id) handleSave();
                                                    else setShowSaveModal(true);
                                                }}>
                                                    <FaSave className="me-2" /> 저장
                                                </Button>
                                                {tabStates.playground.id && (
                                                    <Button variant="outline-primary" className="py-3 fw-bold rounded-4 shadow-sm mt-2 flex-grow-1" style={{ minWidth: '100px' }} onClick={() => {
                                                        const oldTitle = tabStates.playground.title;
                                                        setTabStates(prev => ({ ...prev, playground: { id: null, title: '' } }));
                                                        setTempSaveTitle(oldTitle);
                                                        setShowSaveModal(true);
                                                    }}>
                                                        다른 이름
                                                    </Button>
                                                )}
                                                <Button variant="outline-dark" className="py-3 fw-bold rounded-4 shadow-sm mt-2" style={{ minWidth: '80px' }} onClick={handlePlaygroundDownload}>
                                                    <FaDownload className="me-2" /> 추출
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                            <Col lg={6}>
                                <div className="h-100 d-flex flex-column">
                                    <div className="mb-2 ms-2 small fw-bold text-muted d-flex align-items-center gap-2">
                                        <div className="bg-success rounded-circle" style={{ width: '10px', height: '10px' }}></div>
                                        연성 결과 (Output)
                                    </div>
                                    <div className="flex-grow-1 border shadow-lg rounded-4 overflow-hidden bg-white" style={{ minHeight: '600px', backgroundColor: '#fff' }}>
                                        {previewContent ? (
                                            <iframe
                                                title="forge-preview"
                                                srcDoc={previewContent}
                                                style={{ width: '100%', height: '100%', border: 'none' }}
                                                sandbox="allow-scripts"
                                            />
                                        ) : (
                                            <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted p-5 text-center">
                                                <FaHammer size={50} className="mb-3 opacity-10" />
                                                <h5>용광로가 식어있습니다.</h5>
                                                <p className="small">코드를 입력하고 '연성하기' 버튼을 눌러보세요.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </motion.div>
                </Tab>
            </Tabs>

            {/* --- CRUD 모달 영역 --- */}

            {/* 1. 저장하기 제목 입력 모달 */}
            <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)} centered className="forge-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold"><FaSave className="me-2 text-success" /> 제련품 저장</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pb-4">
                    <Form onSubmit={handleSave}>
                        <Form.Group>
                            <Form.Label className="small fw-bold text-muted">제련품 이름 (제목)</Form.Label>
                            <Form.Control
                                autoFocus
                                type="text"
                                placeholder="예: 멋진 메인 버튼 스타일, API 분석용..."
                                value={tempSaveTitle}
                                onChange={(e) => setTempSaveTitle(e.target.value)}
                                className="rounded-3 border-0 bg-light p-3 shadow-none"
                            />
                        </Form.Group>
                        <div className="d-grid mt-4">
                            <Button variant="success" type="submit" disabled={isSaving} className="rounded-pill py-2 fw-bold">
                                {isSaving ? '제련 중...' : '저장소에 보관하기'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* 2. 불러오기 목록 모달 (제련품 저장소) */}
            <Modal show={showLoadModal} onHide={() => setShowLoadModal(false)} centered size="lg" className="forge-modal">
                <Modal.Header closeButton className="border-bottom px-4">
                    <Modal.Title className="fw-bold"><FaFolderOpen className="me-2 text-primary" /> 제련품 저장소</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {isLoadingList ? (
                        <div className="text-center py-5">
                            <FaCogs className="fa-spin text-muted opacity-25" size={40} />
                            <p className="mt-3 text-muted">저장소를 뒤적거리는 중...</p>
                        </div>
                    ) : savedItems.length > 0 ? (
                        <>
                            <Table hover responsive className="mb-0">
                                <thead className="table-light">
                                    <tr className="small text-muted">
                                        <th className="px-4 py-3">제목</th>
                                        <th className="py-3" style={{ width: '150px' }}>저장일시</th>
                                        <th className="px-4 py-3 text-end" style={{ width: '120px' }}>작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {savedItems
                                        .filter(item => item.extra_data?.tab === activeTab)
                                        .map((item) => (
                                            <tr key={item.id} className="align-middle cursor-pointer" onClick={() => handleLoadItem(item)}>
                                                <td className="px-4 fw-bold">{item.title}</td>
                                                <td className="text-muted small">
                                                    {item.created_at ? new Date(item.created_at).toLocaleString('ko-KR', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : '-'}
                                                </td>
                                                <td className="px-4 text-end" onClick={(e) => e.stopPropagation()}>
                                                    <Button
                                                        variant="link"
                                                        className="text-danger p-1"
                                                        onClick={() => handleDelete(item.id)}
                                                        title="파기"
                                                    >
                                                        <FaTrashAlt />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </Table>
                            {/* 페이징 처리 */}
                            {totalCount > itemsPerPage && (
                                <div className="d-flex justify-content-center py-3 border-top gap-2 bg-light">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="rounded-circle"
                                        style={{ width: '32px', height: '32px', padding: '0' }}
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    >
                                        &lt;
                                    </Button>
                                    <span className="align-self-center small fw-bold mx-2">
                                        Page {currentPage} of {Math.ceil(totalCount / itemsPerPage)}
                                    </span>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="rounded-circle"
                                        style={{ width: '32px', height: '32px', padding: '0' }}
                                        disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                    >
                                        &gt;
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-5">
                            <FaFolderOpen className="text-muted opacity-10" size={60} />
                            <p className="mt-3 text-muted">저장된 제련품이 없습니다.<br />새로운 장비를 제련해 보세요!</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={() => setShowLoadModal(false)} className="rounded-pill px-4 fw-bold">닫기</Button>
                </Modal.Footer>
            </Modal>

            {/* 언어 및 라이브러리 설정 모달 */}
            <Modal show={showOptionsModal} onHide={() => setShowOptionsModal(false)} centered size="lg" className="forge-modal settings-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold h5">Code panel options</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <div className="d-flex" style={{ minHeight: '400px' }}>
                        {/* 왼쪽 사이드바 */}
                        <div className="bg-light border-end" style={{ width: '180px' }}>
                            <div className="p-3">
                                {['html', 'javascript', 'css'].map((tab) => (
                                    <button
                                        key={tab}
                                        className={`btn w-100 text-start mb-2 rounded-3 py-2 px-3 fw-bold d-flex align-items-center gap-2 ${activePanelTab === (tab === 'javascript' ? 'js' : tab) ? 'btn-primary' : 'btn-link text-dark text-decoration-none'}`}
                                        onClick={() => setActivePanelTab(tab === 'javascript' ? 'js' : tab)}
                                    >
                                        <Badge bg={tab === 'html' ? 'danger' : tab === 'javascript' ? 'warning' : 'primary'} style={{ width: '24px', height: '24px', padding: '5px' }}>
                                            {tab[0].toUpperCase()}
                                        </Badge>
                                        <span className="small text-capitalize">{tab}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* 오른쪽 설정 영역 */}
                        <div className="flex-grow-1 p-4 overflow-auto scrollbar-hide" style={{ maxHeight: '500px' }}>
                            {activePanelTab === 'html' ? (
                                <div className="html-settings">
                                    <Row className="mb-4 align-items-center">
                                        <Col sm={3}>
                                            <h6 className="fw-bold text-dark small mb-0">Language</h6>
                                        </Col>
                                        <Col sm={9}>
                                            <div className="d-flex gap-2">
                                                {['HTML', 'HAML', 'Pug'].map((lang) => (
                                                    <Button
                                                        key={lang}
                                                        variant={playgroundSettings.html.lang === lang ? 'warning' : 'light'}
                                                        size="sm"
                                                        className={`rounded-3 px-3 fw-bold border-0 ${playgroundSettings.html.lang === lang ? 'active-lang' : ''}`}
                                                        style={{
                                                            backgroundColor: playgroundSettings.html.lang === lang ? '#f39c12' : '#eee',
                                                            color: playgroundSettings.html.lang === lang ? '#fff' : '#666'
                                                        }}
                                                        onClick={() => setPlaygroundSettings({
                                                            ...playgroundSettings,
                                                            html: { ...playgroundSettings.html, lang }
                                                        })}
                                                    >
                                                        {lang}
                                                    </Button>
                                                ))}
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row className="mb-4 align-items-center">
                                        <Col sm={3}>
                                            <h6 className="fw-bold text-dark small mb-0">Doctype</h6>
                                        </Col>
                                        <Col sm={9}>
                                            <Form.Select
                                                size="sm"
                                                className="rounded-3 border-0 bg-light p-2 shadow-none"
                                                value={playgroundSettings.html.doctype}
                                                onChange={(e) => setPlaygroundSettings({
                                                    ...playgroundSettings,
                                                    html: { ...playgroundSettings.html, doctype: e.target.value }
                                                })}
                                            >
                                                <option>HTML 5</option>
                                                <option>XHTML 1.0 Strict</option>
                                                <option>XHTML 1.0 Transitional</option>
                                                <option>HTML 4.01 Strict</option>
                                                <option>HTML 4.01 Transitional</option>
                                                <option>HTML 4.01 Frameset</option>
                                            </Form.Select>
                                        </Col>
                                    </Row>

                                    <Row className="mb-4 align-items-center">
                                        <Col sm={3}>
                                            <h6 className="fw-bold text-dark small mb-0">Body tag</h6>
                                        </Col>
                                        <Col sm={9}>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder='e.g. class="dark-theme"'
                                                className="rounded-3 border-0 bg-light p-2 shadow-none"
                                                value={playgroundSettings.html.bodyTag}
                                                onChange={(e) => setPlaygroundSettings({
                                                    ...playgroundSettings,
                                                    html: { ...playgroundSettings.html, bodyTag: e.target.value }
                                                })}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            ) : (
                                <>
                                    <section className="mb-4">
                                        <h6 className="fw-bold text-muted small mb-3">Language</h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            {(activePanelTab === 'css' ? ['CSS', 'SCSS', 'Sass', 'Less'] :
                                                ['JavaScript', 'TypeScript', 'Babel', 'React'])
                                                .map((lang) => (
                                                    <Button
                                                        key={lang}
                                                        variant={playgroundSettings[activePanelTab]?.lang === lang ? 'warning' : 'light'}
                                                        size="sm"
                                                        className="rounded-pill px-3 fw-bold border"
                                                        onClick={() => setPlaygroundSettings({
                                                            ...playgroundSettings,
                                                            [activePanelTab]: { ...playgroundSettings[activePanelTab], lang }
                                                        })}
                                                    >
                                                        {lang}
                                                    </Button>
                                                ))}
                                        </div>
                                    </section>

                                    <section>
                                        <h6 className="fw-bold text-muted small mb-3">Extensions / Libraries</h6>
                                        <div className="list-group list-group-flush border rounded-3">
                                            {LIBRARIES[activePanelTab].map((lib) => (
                                                <div key={lib.name} className="list-group-item d-flex justify-content-between align-items-center py-2 px-3">
                                                    <span className="small">{lib.name}</span>
                                                    <Form.Check
                                                        type="switch"
                                                        id={`lib-${lib.name}`}
                                                        checked={lib.url ? playgroundSettings[activePanelTab].libs.includes(lib.url) : false}
                                                        disabled={!lib.url}
                                                        onChange={(e) => {
                                                            const currentLibs = playgroundSettings[activePanelTab].libs;
                                                            const newLibs = e.target.checked
                                                                ? [...currentLibs, lib.url]
                                                                : currentLibs.filter(u => u !== lib.url);
                                                            setPlaygroundSettings({
                                                                ...playgroundSettings,
                                                                [activePanelTab]: { ...playgroundSettings[activePanelTab], libs: newLibs }
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <p className="mt-2 text-muted x-small" style={{ fontSize: '11px' }}>
                                            * 선택한 라이브러리는 연성(Run) 시 자동으로 포함됩니다.
                                        </p>
                                    </section>
                                </>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="dark" onClick={() => setShowOptionsModal(false)} className="rounded-pill px-4">Done</Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .custom-forge-tabs .nav-link {
                    color: #adb5bd;
                    font-weight: 600;
                    padding: 10px 20px;
                    border-radius: 50px;
                    margin: 0 4px;
                    font-size: 14px;
                    transition: all 0.2s ease;
                }
                .custom-forge-tabs .nav-link.active {
                    background-color: #f8f9fa !important;
                    color: #2b2b2b !important;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
                }
                .line-num-cell {
                    user-select: none;
                    vertical-align: top;
                }
                .forge-modal .modal-content {
                    border: none;
                    border-radius: 20px;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.1);
                }
                /* JSON 트리 뷰 개선 스타일 */
                .json-tree-node {
                    font-size: 13px;
                    line-height: 1.6;
                }
                .json-children {
                    margin-left: 18px;
                    border-left: 1px solid #eee;
                    padding-left: 10px;
                }
                .json-row, .json-toggle-row {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .json-key {
                    color: #444;
                    font-weight: 600;
                    white-space: nowrap;
                }
                .json-toggle-icon {
                    width: 12px;
                    font-size: 10px;
                    color: #999;
                    user-select: none;
                }
                .json-value {
                    word-break: break-all;
                }
                .json-meta {
                    font-family: var(--bs-font-monospace);
                    font-weight: 500;
                }
                /* 용광로 설정 모달 스타일 */
                .settings-modal .modal-header {
                    background-color: #fcfcfc;
                    border-bottom: 1px solid #eee !important;
                }
                .settings-modal .modal-footer {
                    background-color: #fcfcfc;
                    border-top: 1px solid #eee !important;
                }
                .active-lang {
                    box-shadow: 0 4px 10px rgba(243, 156, 18, 0.3);
                }
                .html-settings h6 {
                    color: #555 !important;
                    font-size: 13px !important;
                }
                .settings-modal .btn-primary {
                    background-color: #0984e3;
                    border: none;
                }
            `}</style>
        </Container>
    );
};

